import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  api,
  setToken,
  clearToken,
  getToken,
} from "../api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/*
 * ---------------------------------------------------------------------------
 * 1471 Squadron device authentication
 * ---------------------------------------------------------------------------
 *
 * A squadron-owned device is enrolled once.
 *
 * The browser stores:
 *
 *   device_code
 *   device_secret
 *
 * The server stores only the SHA-256 hash of the device secret.
 *
 * Each time the browser needs a new session it exchanges the device
 * code + secret for a short-lived/renewable JWT.
 *
 * Device roles:
 *
 *   staff
 *   attendance_kiosk
 *   cadet_training
 *   display
 *
 * Personal cadet usernames/passwords are not used.
 */

const DEVICE_CODE_KEY = "1471_device_code";
const DEVICE_SECRET_KEY = "1471_device_secret";

function getStoredDeviceCredentials() {
  try {
    const deviceCode = localStorage.getItem(DEVICE_CODE_KEY);
    const deviceSecret = localStorage.getItem(DEVICE_SECRET_KEY);

    if (!deviceCode || !deviceSecret) {
      return null;
    }

    return {
      deviceCode,
      deviceSecret,
    };
  } catch {
    return null;
  }
}

function storeDeviceCredentials(deviceCode, deviceSecret) {
  localStorage.setItem(
    DEVICE_CODE_KEY,
    String(deviceCode || "")
      .trim()
      .toUpperCase()
  );

  localStorage.setItem(
    DEVICE_SECRET_KEY,
    String(deviceSecret || "")
  );
}

function removeDeviceCredentials() {
  localStorage.removeItem(DEVICE_CODE_KEY);
  localStorage.removeItem(DEVICE_SECRET_KEY);
}

function isCredentialFailure(error) {
  const status = error?.response?.status;

  return (
    status === 401 ||
    status === 403 ||
    status === 404 ||
    status === 410
  );
}

export function AuthProvider({ children }) {
  /*
   * `device` is now the primary authenticated identity.
   *
   * `user` is retained for compatibility with the existing staff
   * Dashboard. On a staff computer the backend represents the
   * device as an anonymous admin-style user.
   */
  const [device, setDevice] = useState(null);
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  /*
   * -----------------------------------------------------------------------
   * Load the existing staff user-shaped object
   * -----------------------------------------------------------------------
   *
   * Existing staff pages expect /auth/me and a `user` object.
   *
   * The backend accepts a STAFF device token here and returns a
   * compatibility user object without identifying a real person.
   */
  const loadStaffUser = useCallback(async (currentDevice) => {
    if (!currentDevice || currentDevice.role !== "staff") {
      setUser(null);
      return null;
    }

    const { data } = await api.get("/auth/me");

    setUser(data);

    return data;
  }, []);

  /*
   * -----------------------------------------------------------------------
   * Complete a successful device authentication
   * -----------------------------------------------------------------------
   */
  const applyAuthenticatedDevice = useCallback(
    async (deviceData) => {
      setDevice(deviceData);

      if (deviceData?.role === "staff") {
        return await loadStaffUser(deviceData);
      }

      setUser(null);

      return deviceData;
    },
    [loadStaffUser]
  );

  /*
   * -----------------------------------------------------------------------
   * Exchange stored device credentials for a fresh access token
   * -----------------------------------------------------------------------
   */
  const loginStoredDevice = useCallback(async () => {
    const credentials = getStoredDeviceCredentials();

    if (!credentials) {
      clearToken();
      setDevice(null);
      setUser(null);

      return null;
    }

    try {
      const { data } = await api.post("/device/login", {
        device_code: credentials.deviceCode,
        device_secret: credentials.deviceSecret,
      });

      setToken(data.access_token);

      setAuthError(null);

      await applyAuthenticatedDevice(data.device);

      return data.device;
    } catch (error) {
      /*
       * Only delete the locally stored device credential if the
       * server has explicitly rejected it.
       *
       * A network outage should NOT cause a squadron tablet to lose
       * its enrolment.
       */
      if (isCredentialFailure(error)) {
        clearToken();
        removeDeviceCredentials();

        setDevice(null);
        setUser(null);

        setAuthError(
          error?.response?.data?.detail ||
            "This device is no longer authorised."
        );
      } else {
        setAuthError(
          "The squadron server could not be reached. This device has not been de-authorised."
        );
      }

      return null;
    }
  }, [applyAuthenticatedDevice]);

  /*
   * -----------------------------------------------------------------------
   * Check an existing JWT
   * -----------------------------------------------------------------------
   */
  const checkExistingSession = useCallback(async () => {
    if (!getToken()) {
      return null;
    }

    try {
      const { data } = await api.get("/device/me");

      setAuthError(null);

      await applyAuthenticatedDevice(data);

      return data;
    } catch (error) {
      /*
       * An expired JWT is expected eventually.
       *
       * Don't throw away the permanent device secret. Instead request
       * another JWT using /device/login.
       */
      if (isCredentialFailure(error)) {
        clearToken();

        return await loginStoredDevice();
      }

      setAuthError(
        "The squadron server could not be reached. This device has not been de-authorised."
      );

      return null;
    }
  }, [applyAuthenticatedDevice, loginStoredDevice]);

  /*
   * -----------------------------------------------------------------------
   * Initial startup
   * -----------------------------------------------------------------------
   *
   * Order:
   *
   *   1. Existing JWT?
   *      → validate it.
   *
   *   2. No JWT / expired JWT?
   *      → use stored device credential.
   *
   *   3. No stored device credential?
   *      → Portal will show the device enrolment screen.
   */
  useEffect(() => {
    let active = true;

    const start = async () => {
      try {
        if (getToken()) {
          await checkExistingSession();
        } else if (getStoredDeviceCredentials()) {
          await loginStoredDevice();
        } else {
          clearToken();

          setDevice(null);
          setUser(null);
          setAuthError(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      active = false;
    };
  }, [checkExistingSession, loginStoredDevice]);

  /*
   * -----------------------------------------------------------------------
   * Enrol a new squadron device
   * -----------------------------------------------------------------------
   *
   * Used once when setting up:
   *
   *   STAFF-PC-01
   *   ATTENDANCE-01
   *   CADET-TABLET-01
   *   ...
   *   DISPLAY-01
   *
   * DEVICE_ENROLMENT_KEY is entered during this process.
   *
   * The server returns the unique secret belonging to this device.
   */
  const enrolDevice = useCallback(
    async (deviceCode, enrolmentKey) => {
      const cleanCode = String(deviceCode || "")
        .trim()
        .toUpperCase();

      const cleanKey = String(enrolmentKey || "").trim();

      if (!cleanCode) {
        throw new Error("Enter the device code.");
      }

      if (!cleanKey) {
        throw new Error("Enter the device enrolment key.");
      }

      setAuthError(null);

      try {
        const { data } = await api.post("/device/enrol", {
          device_code: cleanCode,
          enrolment_key: cleanKey,
        });

        if (!data?.device_secret) {
          throw new Error(
            "The server did not return a device credential."
          );
        }

        storeDeviceCredentials(
          data.device.device_code,
          data.device_secret
        );

        setToken(data.access_token);

        await applyAuthenticatedDevice(data.device);

        return data.device;
      } catch (error) {
        const message =
          error?.response?.data?.detail ||
          error?.message ||
          "Device enrolment failed.";

        setAuthError(message);

        throw error;
      }
    },
    [applyAuthenticatedDevice]
  );

  /*
   * -----------------------------------------------------------------------
   * Refresh
   * -----------------------------------------------------------------------
   *
   * Existing staff components already use refresh(), so we keep it.
   *
   * It now refreshes the DEVICE rather than a personal login.
   */
  const refresh = useCallback(async () => {
    try {
      if (!getToken()) {
        return await loginStoredDevice();
      }

      const { data } = await api.get("/device/me");

      setDevice(data);
      setAuthError(null);

      if (data.role === "staff") {
        const staffUser = await loadStaffUser(data);

        return staffUser;
      }

      setUser(null);

      return data;
    } catch (error) {
      if (isCredentialFailure(error)) {
        clearToken();

        return await loginStoredDevice();
      }

      setAuthError(
        "The squadron server could not be reached."
      );

      return null;
    }
  }, [loadStaffUser, loginStoredDevice]);

  /*
   * -----------------------------------------------------------------------
   * Resume this device
   * -----------------------------------------------------------------------
   *
   * Useful if a token has deliberately been cleared but the physical
   * squadron device remains enrolled.
   */
  const resumeDevice = useCallback(async () => {
    setLoading(true);

    try {
      return await loginStoredDevice();
    } finally {
      setLoading(false);
    }
  }, [loginStoredDevice]);

  /*
   * -----------------------------------------------------------------------
   * Runtime sign-out
   * -----------------------------------------------------------------------
   *
   * IMPORTANT:
   *
   * This does NOT forget the physical device enrolment.
   *
   * The squadron device remains registered and can authenticate again.
   *
   * We will eventually remove normal "Log out" controls from cadet,
   * attendance and display devices entirely.
   */
  const logout = useCallback(() => {
    clearToken();

    setDevice(null);
    setUser(null);
    setAuthError(null);
  }, []);

  /*
   * -----------------------------------------------------------------------
   * Permanently forget this browser
   * -----------------------------------------------------------------------
   *
   * This is deliberately separate from logout.
   *
   * After using this function the browser will require the squadron
   * DEVICE_ENROLMENT_KEY before it can be enrolled again.
   *
   * This should eventually be exposed only within staff/device
   * administration controls.
   */
  const forgetDevice = useCallback(() => {
    clearToken();
    removeDeviceCredentials();

    setDevice(null);
    setUser(null);
    setAuthError(null);
  }, []);

  /*
   * -----------------------------------------------------------------------
   * Legacy personal login
   * -----------------------------------------------------------------------
   *
   * Kept as a function temporarily so an old component importing
   * `login` does not crash while we replace the frontend.
   *
   * It deliberately does NOT call /auth/login.
   */
  const login = useCallback(async () => {
    throw new Error(
      "Personal username/password login has been disabled. This computer must be enrolled as a squadron device."
    );
  }, []);

  const hasStoredDevice =
    Boolean(getStoredDeviceCredentials());

  const isStaffDevice =
    device?.role === "staff";

  const isAttendanceDevice =
    device?.role === "attendance_kiosk";

  const isCadetDevice =
    device?.role === "cadet_training";

  const isDisplayDevice =
    device?.role === "display";

  return (
    <AuthContext.Provider
      value={{
        /*
         * Primary device identity
         */
        device,
        setDevice,

        /*
         * Staff compatibility identity
         */
        user,
        setUser,

        /*
         * State
         */
        loading,
        authError,
        setAuthError,
        hasStoredDevice,

        /*
         * Device roles
         */
        isStaffDevice,
        isAttendanceDevice,
        isCadetDevice,
        isDisplayDevice,

        /*
         * Authentication actions
         */
        enrolDevice,
        resumeDevice,
        refresh,
        logout,
        forgetDevice,

        /*
         * Temporary backwards compatibility only
         */
        login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}